// src/components/DecimalGrid/DecimalGrid.jsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import "./DecimalGrid.css";

/**
 * props:
 *  - gridType: "10x10" | "10" | "100" (假如 "100" 也想表示 "10x10")
 *  - filledCount: 初始涂色数量（列优先）
 *  - onFractionChange: function(fractionStr) => void
 */
const DecimalGrid = ({ gridType = "100", filledCount = 0, gridModify = false, onFractionChange = () => {} }) => {

    const is10x10 = gridType === "100";
    // totalCount => 10x10=100格, 10=10格
    const totalCount = is10x10 ? 100 : 10;

    // 存储每个格子的涂色状态：true=已涂色, false=未涂色
    const [filledArray, setFilledArray] = useState(Array(totalCount).fill(false));

    // 是否在拖动中
    const [isDrawing, setIsDrawing] = useState(false);
    // 当前手势是“消色”还是“涂色”
    const [isErasing, setIsErasing] = useState(false);

    // 用于获取 svg DOM
    const svgRef = useRef(null);

    // ------------------- 1. 初始化：列优先填色 -------------------
    useEffect(() => {
        const arr = Array(totalCount).fill(false);
        for (let i = 0; i < filledCount && i < totalCount; i++) {
            if (is10x10) {
                // 列优先：col = floor(i/10), row = i%10 => index = row*10 + col
                const col = Math.floor(i / 10);
                const row = i % 10;
                const index = row * 10 + col;
                arr[index] = true;
            } else {
                // 只有10个 => 前 i 个
                arr[i] = true;
            }
        }
        setFilledArray(arr);
    }, [filledCount, is10x10, totalCount]);

    // ------------------- 2. 根据(x,y)计算对应格子 index -------------------
    const getIndexByCoord = useCallback((x, y) => {
        const svg = svgRef.current;
        if (!svg) return -1;
        const rect = svg.getBoundingClientRect();
        const scaleX = 320 / rect.width; // 计算水平缩放比例
        const scaleY = 320 / rect.height; // 计算垂直缩放比例

        const scaledX = x * scaleX;
        const scaledY = y * scaleY;

        if (is10x10) {
            // 10x10，每格 32×32
            const size = 32;
            const col = Math.floor(scaledX / size);
            const row = Math.floor(scaledY / size);
            if (col < 0 || col >= 10 || row < 0 || row >= 10) return -1;
            return row * 10 + col;
        } else {
            // "10" => 10条，每条宽 32，高 320
            const barWidth = 32;
            const col = Math.floor(scaledX / barWidth);
            if (col < 0 || col >= 10) return -1;
            return col;
        }
    }, [is10x10, svgRef]);

    // ------------------- 3. 强制设置某格子的涂色/未涂色 -------------------
    const applyColorChange = useCallback((index, fill) => {
        setFilledArray((prev) => {
            const newArr = [...prev];
            newArr[index] = fill;
            return newArr;
        });
    }, []);

    // ------------------- 4. pointerDown：决定模式 & 处理首个格子 -------------------
    const handlePointerDown = (e) => {
        e.preventDefault();
        setIsDrawing(true);

        const svgRect = svgRef.current?.getBoundingClientRect();
        if (!svgRect) return;

        const x = e.clientX - svgRect.left;
        const y = e.clientY - svgRect.top;

        const index = getIndexByCoord(x, y);
        if (index === -1) return;

        const currentFilled = filledArray[index];
        // 如果已涂色 => 本次手势是“消色”，否则“涂色”
        setIsErasing(currentFilled);

        // 第一个格子 => 如果已涂色就消色, 否则涂色
        applyColorChange(index, !currentFilled);
    };

    // ------------------- 5. pointerMove：若isDrawing，则持续处理 -------------------
    const handlePointerMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault();

        const svgRect = svgRef.current?.getBoundingClientRect();
        if (!svgRect) return;

        const x = e.clientX - svgRect.left;
        const y = e.clientY - svgRect.top;

        const index = getIndexByCoord(x, y);
        if (index === -1) return;

        // 若是消色模式 => false，否则 => true
        applyColorChange(index, !isErasing);
    };

    // ------------------- 6. pointerUp / pointerLeave：结束拖动 -------------------
    const handlePointerUp = () => {
        setIsDrawing(false);
    };

    // ------------------- 7. 渲染 SVG: 10x10 or 10 条 -------------------
    const renderRectangles = () => {
        if (is10x10) {
            const size = 32;
            const rects = [];
            for (let row = 0; row < 10; row++) {
                for (let col = 0; col < 10; col++) {
                    const index = row * 10 + col;
                    const fillColor = filledArray[index] ? "#f4511e" : "#ffffff";
                    rects.push(
                        <rect
                            key={index}
                            x={col * size}
                            y={row * size}
                            width={size}
                            height={size}
                            fill={fillColor}
                            stroke="#289dcf"
                            strokeWidth={1}
                        />
                    );
                }
            }
            return rects;
        } else {
            // "10" => 10竖条
            const barWidth = 32;
            const barHeight = 320;
            const bars = [];
            for (let col = 0; col < 10; col++) {
                const index = col;
                const fillColor = filledArray[index] ? "#f4511e" : "#ffffff";
                bars.push(
                    <rect
                        key={index}
                        x={col * barWidth}
                        y={0}
                        width={barWidth}
                        height={barHeight}
                        fill={fillColor}
                        stroke="#289dcf"
                        strokeWidth={1}
                    />
                );
            }
            return bars;
        }
    };

    // ------------------- 8. 计算“已涂色的小数” -------------------
    // 当前已涂色数量
    const filledCountNow = filledArray.filter(Boolean).length;
    // 对于10x10 => 每个格=0.01； 对于10 => 每个格=0.1
    const fraction = is10x10
        ? filledCountNow * 0.01
        : filledCountNow * 0.1;
    // 格式化到 2 位小数
    const fractionStr = is10x10
        ? fraction.toFixed(2)
        : fraction.toFixed(1);

    // ------------------- 9. 通知外部 fractionStr 变化 -------------------
    useEffect(() => {
        if (onFractionChange) {
            onFractionChange(fractionStr);
        }
    }, [fractionStr, onFractionChange]);

    return (
        <div className="decimal-grid-container">
            {/*<h2>
                我是 {is10x10 ? "10×10" : "10"} 网格，
                涂色小数 = {fractionStr}
                /!* 例如：填10个 => 0.10； 填1个 => 0.01(10x10) or 0.1(10) *!/*
            </h2>*/}

            <svg
                ref={svgRef}
                className="decimal-grid-svg"
                width="100%"
                height="100%"
                viewBox={`0 0 320 320`}
                style={{ touchAction: "none", userSelect: "none" }}
                {...(gridModify
                    ? {
                        onPointerDown: handlePointerDown,
                        onPointerMove: handlePointerMove,
                        onPointerUp: handlePointerUp,
                        onPointerLeave: handlePointerUp,
                    } : {})}
            >
                {renderRectangles()}
            </svg>
        </div>
    );
};

export default DecimalGrid;